(() => {
  "use strict";

  if (window.__paloAltoPriceModeObserverLoaded) {
    return;
  }

  window.__paloAltoPriceModeObserverLoaded = true;

  const COUNTRY_STORAGE_PREFIX = "palo-alto:price-mode-country";
  const validModes = new Set(["INCL_TAX", "EXCL_TAX", "UNKNOWN"]);
  const previousStateByKey = new Map();
  const mismatchCache = new Set();

  function pushDataLayerEvent(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload,
    });
  }

  function getPriceBlocks(root) {
    const scope = root instanceof Element ? root : document;

    return Array.from(scope.querySelectorAll("[data-price-wrapper][data-price-mode]"))
      .filter((block) => !block.hasAttribute("data-atc-button"));
  }

  function normalizePayload(block) {
    if (!(block instanceof HTMLElement)) {
      return null;
    }

    return {
      product_id: block.dataset.productId || null,
      variant_id: block.dataset.variantId || null,
      country_code: block.dataset.countryCode || "unknown",
      market_handle: block.dataset.marketHandle || "unknown",
      currency: block.dataset.currency || null,
      price_mode: block.dataset.priceMode || "UNKNOWN",
      price_mode_source: block.dataset.priceModeSource || "unknown",
      price_mode_signal_conflict:
        block.dataset.priceModeSignalConflict || "false",
      taxes_included: block.dataset.taxesIncluded || "unknown",
      surface: block.dataset.surface || "pdp",
      template: block.dataset.template || null,
    };
  }

  function getPayloadKey(payload) {
    return [
      payload.surface || "unknown",
      payload.product_id || "unknown",
      payload.template || "unknown",
    ].join(":");
  }

  function emitMismatch(payload, reason) {
    const key = [
      reason,
      payload.surface || "unknown",
      payload.product_id || "unknown",
      payload.variant_id || "unknown",
      payload.country_code || "unknown",
    ].join(":");

    if (mismatchCache.has(key)) {
      return;
    }

    mismatchCache.add(key);
    pushDataLayerEvent("price_mode_mismatch", {
      ...payload,
      reason,
    });
  }

  function handleCountryChange(payload, sentCountryEvents) {
    if (!window.sessionStorage) {
      return;
    }

    try {
      const storageKey = `${COUNTRY_STORAGE_PREFIX}:${payload.surface}`;
      const previousCountryCode = window.sessionStorage.getItem(storageKey);
      const countryKey = `${payload.surface}:${payload.country_code}`;

      if (
        previousCountryCode &&
        previousCountryCode !== payload.country_code &&
        !sentCountryEvents.has(countryKey)
      ) {
        pushDataLayerEvent("price_mode_country_change", payload);
        sentCountryEvents.add(countryKey);
      }

      window.sessionStorage.setItem(storageKey, payload.country_code);
    } catch (error) {
      emitMismatch(payload, "country_storage_unavailable");
    }
  }

  function scanPriceModes(root, source) {
    const sentCountryEvents = new Set();

    getPriceBlocks(root).forEach((block) => {
      const payload = normalizePayload(block);

      if (!payload) {
        return;
      }

      if (!validModes.has(payload.price_mode)) {
        emitMismatch(payload, "invalid_price_mode");
        return;
      }

      if (payload.taxes_included === "unknown") {
        emitMismatch(payload, "taxes_included_unknown");
      }

      if (payload.price_mode_signal_conflict === "true") {
        emitMismatch(payload, "price_mode_signal_conflict");
      }

      handleCountryChange(payload, sentCountryEvents);

      const payloadKey = getPayloadKey(payload);
      const previousPayload = previousStateByKey.get(payloadKey);

      if (!previousPayload) {
        pushDataLayerEvent("price_mode_resolved", payload);
      } else if (
        source === "variant_change" &&
        previousPayload.variant_id !== payload.variant_id
      ) {
        pushDataLayerEvent("price_mode_variant_change", {
          ...payload,
          previous_variant_id: previousPayload.variant_id,
          next_variant_id: payload.variant_id,
        });
      }

      previousStateByKey.set(payloadKey, payload);
    });
  }

  function scheduleScan(root, source) {
    window.requestAnimationFrame(() => {
      scanPriceModes(root, source);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      scheduleScan(document, "dom_ready");
    });
  } else {
    scheduleScan(document, "dom_ready");
  }

  document.addEventListener("theme:product-info:loaded", (event) => {
    const root = event.target instanceof Element ? event.target : document;
    scheduleScan(root, "product_info_loaded");
  });

  if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
    window.subscribe(theme.PUB_SUB_EVENTS.variantChange, () => {
      scheduleScan(document, "variant_change");
    });
  }
})();
